import React, { useEffect, useState } from 'react';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { getAppName } from 'src/utils/app';
import _omit from 'lodash/omit';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import BeaconMetricColumnChart, {
  BeaconMetricColumnChartProps
} from 'src/components/BeaconV2Components/BeaconMetricColumnChart/BeaconMetricColumnChart';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { UseAdvancedSearchDataArgs } from 'src/serverProxy/useAdvancedSearchData';

export interface BeaconDoubleDropdownMetricColumnChartProps
  extends Omit<BeaconMetricColumnChartProps, 'indexName' | 'fieldName' | 'useAdvancedSearchDataArgs'> {
  fields: {
    fieldName: string;
    label?: string;
  }[];
  useAdvancedSearchDataArgs: Partial<Omit<UseAdvancedSearchDataArgs, 'indexName'>>;
  groupByFields: {
    id: string;
    label: string;
    indexName: string;
  }[];
  showSingleGroupBy?: boolean;
  /**
   * Optionally override the selected field/metric from the parent component.
   * Defaults to the first field in the fields array.
   */
  selectedField?: string;
  selectedGroupByField?: string;
  selectedGroupByCallback?: (selectedGroupByField: string) => void;
}

const getField = (indexName: string, fieldName: string) => {
  return INDEX_FIELDS.getField(getAppName(), indexName, fieldName);
};

/**
 * Query advanced search data with two dropdowns. The first is for metric fields and
 * the second is for group by fields.
 */
export default function BeaconDoubleDropdownMetricColumnChart(props: BeaconDoubleDropdownMetricColumnChartProps) {
  const { fields, groupByFields, showSingleGroupBy, selectedField, selectedGroupByField } = props;
  const [selectedFieldName, setSelectedFieldName] = useState(selectedField || fields[0].fieldName);
  const [selectedGroupByFieldName, setSelectedGroupByFieldName] = useState(selectedGroupByField || groupByFields[0].id);
  useEffect(() => {
    if (selectedField) {
      setSelectedFieldName(selectedField);
    }
  }, [selectedField]);

  const groupByFieldToIndex = groupByFields.reduce((acc, field) => {
    return {
      ...acc,
      [field.id]: field.indexName
    };
  }, {});

  const indexName = groupByFieldToIndex[selectedGroupByFieldName];

  return (
    <BeaconMetricColumnChart
      {...props}
      indexName={indexName}
      fieldName={selectedFieldName}
      useAdvancedSearchDataArgs={{
        groupByField: selectedGroupByFieldName,
        fields: [{ name: selectedFieldName }],
        ...(props.useAdvancedSearchDataArgs ? props.useAdvancedSearchDataArgs : {})
      }}
      beaconChartWithLegendPropsBuilder={(legendProps) => ({
        ..._omit(legendProps, 'singleDropdownProps'),
        ...props?.beaconChartWithLegendPropsBuilder?.(legendProps, selectedFieldName, selectedGroupByFieldName),
        title: showSingleGroupBy ? (
          <Flex gap="sm" alignItems="center">
            <SlDropdownMenu
              buttonVariant="title"
              options={fields.map((field) => ({
                id: field.fieldName,
                label: field.label || getField(indexName, field.fieldName).displayName
              }))}
              defaultLabel=""
              onChange={(option) => setSelectedFieldName(option.id)}
              selectedId={selectedFieldName}
            />
            <Text variant="h4">by</Text>
            <Text variant="h4">{groupByFields[0]?.label ?? ''}</Text>
          </Flex>
        ) : (
          <Flex gap="sm" alignItems="center">
            <SlDropdownMenu
              buttonVariant="title"
              options={fields.map((field) => ({
                id: field.fieldName,
                label: field.label || getField(indexName, field.fieldName).displayName
              }))}
              defaultLabel=""
              onChange={(option) => setSelectedFieldName(option.id)}
              selectedId={selectedFieldName}
            />
            <Text variant="h4">by</Text>
            <SlDropdownMenu
              buttonVariant="title"
              options={groupByFields}
              defaultLabel=""
              onChange={(option) => {
                setSelectedGroupByFieldName(option.id);
                if (props.selectedGroupByCallback) {
                  props.selectedGroupByCallback(option.id);
                }
              }}
              selectedId={selectedGroupByFieldName}
            />
          </Flex>
        )
      })}
    />
  );
}
